/* eslint-disable max-len */
import { computed, effect, inject, Injectable, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { AppConfigsService } from '@core/app-configs/services/app-configs.service';
import { OnlinePlayGlobalLobbyService } from '@core/online-play/services/online-play-global-lobby.service';
import { OnlinePlayMaintenanceStateService } from '@core/online-play/services/online-play-maintenance-state.service';
import { OnlinePlayOnlineGameService } from '@core/online-play/services/online-play-online-game.service';
import { OnlinePlayPreGameService } from '@core/online-play/services/online-play-pre-game.service';
import { OnlinePlaySocketService } from '@core/online-play/services/online-play-socket.service';
import { MaintenanceApiService } from '@dc-api/maintenance.api.service';
import { OnlineMaintenanceBase } from '@dc-core/dc-backend/dc-classes';
import { MaintenanceStatusUpdatedEvent } from '@dc-core/dc-backend/dc-events';
import { AppConfigsResponse } from '@dc-core/dc-backend/dc-responses';
import { AlertOptions, AlertService } from '@dc-core/dc-services/alert.service';
import { NavController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { GamesLeaveBusterService } from 'src/app/modules/games/services/games-leave-buster.service';
import { RankedQueueService } from 'src/app/modules/ranking/services/ranked-queue.service';
import { EchoService } from 'src/app/services/echo.service';
import { ClockService } from 'src/app/shared/services/clock.service';
import { ModalService } from 'src/app/shared/services/modal.service';

export type MaintenanceArea = keyof AppConfigsResponse['maintenances'];

type WarningMinute = 60 | 30 | 15 | 5;
type MaintenanceConfig = AppConfigsResponse['maintenances'][MaintenanceArea];

type MaintenanceScheduleState = {
    warningTimeout: ReturnType<typeof setTimeout> | null;
    softMaintenanceStartTimeout: ReturnType<typeof setTimeout> | null;
    hardMaintenanceStartTimeout: ReturnType<typeof setTimeout> | null;
    maintenanceEndTimeout: ReturnType<typeof setTimeout> | null;
    maintenanceRefreshTimeout: ReturnType<typeof setTimeout> | null;
    maintenanceRefreshInProgress: boolean;
    scheduledSoftMaintenanceKey: string | null;
    scheduledHardMaintenanceKey: string | null;
    scheduledMaintenanceEndKey: string | null;
    scheduledWarningKey: string | null;
    shownWarnings: Set<number>;
    forcedSoftMaintenanceKey: string | null;
    forcedHardMaintenanceKey: string | null;
    shownSoftMaintenanceContinuationKey: string | null;
    blockedForSoftMaintenance: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class OnlineMaintenanceSchedulerService {
    private appConfigsService: AppConfigsService = inject(AppConfigsService);
    private maintenanceApiService: MaintenanceApiService = inject(MaintenanceApiService);
    private alertService: AlertService = inject(AlertService);
    private echoService: EchoService = inject(EchoService);
    private gamesLeaveBusterService: GamesLeaveBusterService = inject(GamesLeaveBusterService);
    private rankedQueueService: RankedQueueService = inject(RankedQueueService);
    private onlinePlayGlobalLobbyService: OnlinePlayGlobalLobbyService = inject(OnlinePlayGlobalLobbyService);
    private onlinePlayOnlineGameService: OnlinePlayOnlineGameService = inject(OnlinePlayOnlineGameService);
    private onlinePlayMaintenanceStateService: OnlinePlayMaintenanceStateService = inject(
        OnlinePlayMaintenanceStateService
    );
    private onlinePlayPreGameService: OnlinePlayPreGameService = inject(OnlinePlayPreGameService);
    private onlinePlaySocketService: OnlinePlaySocketService = inject(OnlinePlaySocketService);
    private translateService: TranslateService = inject(TranslateService);
    private navController: NavController = inject(NavController);
    private modalService: ModalService = inject(ModalService);
    private router: Router = inject(Router);
    private clockService: ClockService = inject(ClockService);

    private initialized: WritableSignal<boolean> = signal(false);
    private maintenanceStates: Record<MaintenanceArea, MaintenanceScheduleState> = {
        app: this.createMaintenanceScheduleState(),
        online_play: this.createMaintenanceScheduleState(),
        gameplay: this.createMaintenanceScheduleState(),
        ranking: this.createMaintenanceScheduleState(),
    };

    private readonly warningMinutes: WarningMinute[] = [60, 30, 15, 5];
    private readonly earliestLocalWarningMinutes: WarningMinute = 60;
    private readonly minMaintenanceRefreshIntervalMs: number = 5 * 60 * 1000;
    private readonly maxMaintenanceRefreshIntervalMs: number = 7 * 60 * 1000;
    private readonly minMaintenanceEndJitterMs: number = 10 * 1000;
    private readonly maxMaintenanceEndJitterMs: number = 30 * 1000;

    private clockIsReady: Signal<boolean> = computed(() => this.clockService.clockIsReady());
    private appMaintenance: Signal<MaintenanceConfig | undefined> = computed(
        () => this.appConfigsService.appConfigs().maintenances?.app
    );
    private onlinePlayMaintenance: Signal<MaintenanceConfig | undefined> = computed(
        () => this.appConfigsService.appConfigs().maintenances?.online_play
    );
    private gameplayMaintenance: Signal<MaintenanceConfig | undefined> = computed(
        () => this.appConfigsService.appConfigs().maintenances?.gameplay
    );
    private rankingMaintenance: Signal<MaintenanceConfig | undefined> = computed(
        () => this.appConfigsService.appConfigs().maintenances?.ranking
    );

    constructor() {
        effect(() => {
            const initialized = this.initialized();
            const clockIsReady = this.clockIsReady();
            const appMaintenance = this.appMaintenance();
            const onlinePlayMaintenance = this.onlinePlayMaintenance();
            const rankingMaintenance = this.rankingMaintenance();
            const gameplayMaintenance = this.gameplayMaintenance();

            if (!initialized || !clockIsReady) {
                return;
            }

            untracked(() => {
                const now = this.clockService.getNow();
                if (now == null) return;

                this.syncMaintenanceSchedule('app', appMaintenance, now);
                this.syncMaintenanceSchedule('online_play', onlinePlayMaintenance, now);
                this.syncMaintenanceSchedule('ranking', rankingMaintenance, now);
                this.syncMaintenanceSchedule('gameplay', gameplayMaintenance, now);
            });
        });

        effect(() => {
            const initialized = this.initialized();
            const appMaintenance = this.appMaintenance();
            const onlinePlayMaintenance = this.onlinePlayMaintenance();
            const rankingMaintenance = this.rankingMaintenance();
            const gameplayMaintenance = this.gameplayMaintenance();

            this.onlinePlayMaintenanceStateService.inOnlineGame();
            this.onlinePlayMaintenanceStateService.inPreGame();
            this.rankedQueueService.inQueue();
            this.rankedQueueService.queuePairConfirmationState();

            if (!initialized) {
                return;
            }

            untracked(() => {
                if (appMaintenance?.in_maintenance) {
                    const maintenanceKey =
                        this.getSoftMaintenanceStartDate(appMaintenance)?.toISOString() ?? 'app_maintenance';
                    this.forceStartHardMaintenance('app', maintenanceKey, appMaintenance);
                    this.scheduleMaintenanceRefresh('app');
                }
                this.handleActiveSoftMaintenanceContext('online_play', onlinePlayMaintenance);
                this.handleActiveSoftMaintenanceContext('ranking', rankingMaintenance);
                this.handleActiveSoftMaintenanceContext('gameplay', gameplayMaintenance);
            });
        });
    }

    public init(): void {
        if (this.initialized()) return;

        this.initialized.set(true);

        this.initStatusListener();
    }

    private syncMaintenanceSchedule(
        area: MaintenanceArea,
        maintenance: MaintenanceConfig | undefined,
        now: number
    ): void {
        const state = this.maintenanceStates[area];

        if (!maintenance) {
            this.clearTimers(area);
            this.clearMaintenanceRefreshTimeout(area);
            this.clearScheduledMaintenanceState(area);
            return;
        }

        const softStartsAt = this.getSoftMaintenanceStartDate(maintenance);
        const hardStartsAt = area === 'app' ? softStartsAt : this.getHardMaintenanceStartDate(maintenance);
        const softMaintenanceKey = softStartsAt?.toISOString() ?? null;
        const hardMaintenanceKey = hardStartsAt?.toISOString() ?? null;
        const endsAt = this.getMaintenanceEndDate(maintenance);

        if (endsAt && endsAt.getTime() <= now) {
            this.finishMaintenance(area, true);
            return;
        }

        if (
            !maintenance.in_maintenance &&
            state.forcedSoftMaintenanceKey &&
            (!softStartsAt || softStartsAt.getTime() > now)
        ) {
            this.finishMaintenance(area, false);
        }

        if (maintenance.in_maintenance) {
            if (
                state.scheduledSoftMaintenanceKey !== softMaintenanceKey ||
                state.scheduledHardMaintenanceKey !== hardMaintenanceKey
            ) {
                this.clearTimers(area);
                if (state.scheduledHardMaintenanceKey !== hardMaintenanceKey) {
                    this.resetHardMaintenanceState(area);
                    state.forcedHardMaintenanceKey = null;
                }
                state.scheduledSoftMaintenanceKey = softMaintenanceKey;
                state.scheduledHardMaintenanceKey = hardMaintenanceKey;
            }

            state.forcedSoftMaintenanceKey = softMaintenanceKey ?? 'in_maintenance';

            if (area === 'app') {
                this.forceStartHardMaintenance(area, hardMaintenanceKey ?? 'app_maintenance', maintenance);
            } else if (hardStartsAt) {
                if (hardStartsAt.getTime() <= now) {
                    this.forceStartHardMaintenance(area, hardMaintenanceKey ?? 'hard_maintenance', maintenance);
                } else {
                    this.scheduleHardMaintenanceStart(
                        area,
                        hardStartsAt.getTime(),
                        hardMaintenanceKey ?? 'hard_maintenance',
                        now
                    );
                    this.scheduleWarnings(area, hardStartsAt.getTime(), maintenance, now);
                }
            }

            this.scheduleMaintenanceEnd(area, endsAt);
            this.scheduleMaintenanceRefresh(area);
            return;
        }

        this.clearMaintenanceRefreshTimeout(area);

        if (
            state.scheduledSoftMaintenanceKey !== softMaintenanceKey ||
            state.scheduledHardMaintenanceKey !== hardMaintenanceKey
        ) {
            this.clearTimers(area);
            this.resetHardMaintenanceState(area);
            state.scheduledSoftMaintenanceKey = softMaintenanceKey;
            state.scheduledHardMaintenanceKey = hardMaintenanceKey;
            state.forcedSoftMaintenanceKey = null;
            state.forcedHardMaintenanceKey = null;
        }

        if (!softStartsAt && !hardStartsAt) {
            this.clearTimers(area);
            return;
        }

        if (softStartsAt) {
            if (softStartsAt.getTime() <= now) {
                this.forceStartSoftMaintenance(area, softMaintenanceKey ?? 'soft_maintenance', maintenance);
            } else {
                this.scheduleSoftMaintenanceStart(
                    area,
                    softStartsAt.getTime(),
                    softMaintenanceKey ?? 'soft_maintenance',
                    now
                );
            }
        }

        if (hardStartsAt) {
            if (hardStartsAt.getTime() <= now) {
                this.forceStartHardMaintenance(area, hardMaintenanceKey ?? 'hard_maintenance', maintenance);
                return;
            }

            this.scheduleHardMaintenanceStart(
                area,
                hardStartsAt.getTime(),
                hardMaintenanceKey ?? 'hard_maintenance',
                now
            );
        }

        const warningStartsAt = this.getMaintenanceWarningStartDate(maintenance, now);
        if (warningStartsAt && warningStartsAt.getTime() - now <= this.earliestLocalWarningMinutes * 60 * 1000) {
            this.scheduleWarnings(area, warningStartsAt.getTime(), maintenance, now);
        }

        if (area === 'app' && maintenance.show_notice && softStartsAt && softStartsAt.getTime() > now) {
            this.syncWarningScheduleState(area, softStartsAt.getTime());
            if (state.shownWarnings.size === 0) {
                const minutes = Math.ceil((softStartsAt.getTime() - now) / 60_000);
                state.shownWarnings.add(minutes);
                this.showMaintenanceWarning(area, minutes, maintenance);
            }
        }
    }

    private scheduleWarnings(
        area: MaintenanceArea,
        maintenanceStartsAt: number,
        maintenance: MaintenanceConfig,
        now: number
    ): void {
        const state = this.maintenanceStates[area];
        this.syncWarningScheduleState(area, maintenanceStartsAt);

        if (state.warningTimeout) {
            return;
        }

        const nextWarningMinute = this.getNextWarningMinute(area, maintenanceStartsAt, now);
        if (nextWarningMinute == null) {
            return;
        }

        const delay = maintenanceStartsAt - now - nextWarningMinute * 60 * 1000;
        state.warningTimeout = setTimeout(
            () => {
                state.warningTimeout = null;
                const currentTime: number | null = this.clockService.getNow();
                if (currentTime == null) return;

                if (
                    !state.shownWarnings.has(nextWarningMinute) &&
                    currentTime < maintenanceStartsAt &&
                    currentTime >= maintenanceStartsAt - nextWarningMinute * 60 * 1000
                ) {
                    state.shownWarnings.add(nextWarningMinute);
                    this.showMaintenanceWarning(area, nextWarningMinute, maintenance);
                }

                this.scheduleWarnings(area, maintenanceStartsAt, maintenance, currentTime);
            },
            Math.min(Math.max(delay, 0), 2_147_483_647)
        );
    }

    private scheduleSoftMaintenanceStart(
        area: MaintenanceArea,
        maintenanceStartsAt: number,
        maintenanceKey: string,
        now: number
    ): void {
        const state = this.maintenanceStates[area];

        if (state.softMaintenanceStartTimeout || state.forcedSoftMaintenanceKey === maintenanceKey) {
            return;
        }

        state.softMaintenanceStartTimeout = setTimeout(
            () => {
                state.softMaintenanceStartTimeout = null;
                const currentTime: number | null = this.clockService.getNow();
                if (currentTime != null) this.syncMaintenanceSchedule(area, this.getMaintenance(area), currentTime);
            },
            Math.min(Math.max(maintenanceStartsAt - now, 0), 2_147_483_647)
        );
    }

    private scheduleHardMaintenanceStart(
        area: MaintenanceArea,
        maintenanceStartsAt: number,
        maintenanceKey: string,
        now: number
    ): void {
        const state = this.maintenanceStates[area];

        if (state.hardMaintenanceStartTimeout || state.forcedHardMaintenanceKey === maintenanceKey) {
            return;
        }

        state.hardMaintenanceStartTimeout = setTimeout(
            () => {
                state.hardMaintenanceStartTimeout = null;
                const currentTime: number | null = this.clockService.getNow();
                if (currentTime != null) this.syncMaintenanceSchedule(area, this.getMaintenance(area), currentTime);
            },
            Math.min(Math.max(maintenanceStartsAt - now, 0), 2_147_483_647)
        );
    }

    private scheduleMaintenanceEnd(area: MaintenanceArea, maintenanceEndsAt: Date | null): void {
        const state = this.maintenanceStates[area];
        const maintenanceEndKey = maintenanceEndsAt?.toISOString() ?? null;

        if (state.scheduledMaintenanceEndKey === maintenanceEndKey && state.maintenanceEndTimeout) {
            return;
        }

        if (state.scheduledMaintenanceEndKey !== maintenanceEndKey) {
            this.clearMaintenanceEndTimeout(area);
            state.scheduledMaintenanceEndKey = maintenanceEndKey;
        }

        if (!maintenanceEndsAt) {
            return;
        }

        const now = this.clockService.getNow();
        if (now == null) return;

        const delay = this.getMaintenanceEndDelay(maintenanceEndsAt, now);

        if (delay <= 0) {
            this.handleMaintenanceFinishedFromEndDate(area);
            return;
        }

        state.maintenanceEndTimeout = setTimeout(
            () => {
                state.maintenanceEndTimeout = null;
                this.scheduleMaintenanceEnd(area, this.getMaintenanceEndDate(this.getMaintenance(area)));
            },
            Math.min(delay, 2_147_483_647)
        );
    }

    private scheduleMaintenanceRefresh(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.maintenanceRefreshTimeout || state.maintenanceRefreshInProgress) {
            return;
        }

        state.maintenanceRefreshTimeout = setTimeout(() => {
            state.maintenanceRefreshTimeout = null;
            void this.refreshMaintenanceConfig(area);
        }, this.getMaintenanceRefreshDelay());
    }

    private getMaintenanceRefreshDelay(): number {
        return this.getRandomDelay(this.minMaintenanceRefreshIntervalMs, this.maxMaintenanceRefreshIntervalMs);
    }

    private getMaintenanceEndDelay(maintenanceEndsAt: Date, now: number): number {
        const delay = maintenanceEndsAt.getTime() - now;
        if (delay <= 0) return 0;

        return delay + this.getRandomDelay(this.minMaintenanceEndJitterMs, this.maxMaintenanceEndJitterMs);
    }

    private getRandomDelay(minDelay: number, maxDelay: number): number {
        const jitterRange = maxDelay - minDelay;
        return minDelay + Math.floor(Math.random() * (jitterRange + 1));
    }

    private async refreshMaintenanceConfig(area: MaintenanceArea): Promise<void> {
        const state = this.maintenanceStates[area];

        if (!this.getMaintenance(area)?.in_maintenance) {
            return;
        }

        let refreshFailed = false;

        state.maintenanceRefreshInProgress = true;

        try {
            await this.loadMaintenances();
        } catch {
            refreshFailed = true;
        } finally {
            state.maintenanceRefreshInProgress = false;
        }

        if (refreshFailed) {
            this.scheduleMaintenanceRefresh(area);
            return;
        }

        if (!this.getMaintenance(area)?.in_maintenance) {
            this.handleMaintenanceFinishedFromConfig(area);
            return;
        }

        this.scheduleMaintenanceRefresh(area);
    }

    private async loadMaintenances(): Promise<void> {
        const requestedMaintenances = this.appConfigsService.appConfigs().maintenances;
        const { data } = await this.maintenanceApiService.getMaintenances({});

        this.appConfigsService.updateMaintenances(data.maintenances, requestedMaintenances);
    }

    private showMaintenanceWarning(
        area: MaintenanceArea,
        minutesUntilMaintenance: number,
        maintenance: MaintenanceConfig | undefined
    ): void {
        if (!this.shouldShowMaintenanceAlerts(area)) return;

        const warningStartsAt = this.getMaintenanceWarningStartDate(
            this.getMaintenance(area),
            this.clockService.getNow()
        );
        const remainingMinutes = this.getMinutesUntilMaintenance(warningStartsAt) ?? minutesUntilMaintenance;
        if (remainingMinutes <= 0) return;

        this.alertService.createAlert({
            id: `${this.getMaintenanceAlertIdPrefix(area)}-maintenance-warning-${minutesUntilMaintenance}`,
            title: this.getMaintenanceWarningTitle(area, remainingMinutes),
            text: maintenance?.notice_message?.trim() || this.getMaintenanceWarningFallbackText(area),
            ...this.getMaintenanceMoreInfoAction(maintenance),
            icon: 'warning',
            timer: 10000,
            showCloseButton: true,
        });
    }

    private forceStartSoftMaintenance(
        area: MaintenanceArea,
        maintenanceKey: string,
        maintenance: MaintenanceConfig | undefined,
        softMaintenanceStartsAt: string | null = null,
        hardMaintenanceStartsAt: string | null = null,
        maintenanceEndsAt: string | null = null
    ): void {
        const state = this.maintenanceStates[area];
        const effectiveMaintenanceEndsAt = maintenanceEndsAt ?? maintenance?.maintenance_ends_at ?? null;

        if (state.forcedSoftMaintenanceKey === maintenanceKey) {
            this.setMaintenanceToTrue(
                area,
                softMaintenanceStartsAt,
                hardMaintenanceStartsAt,
                effectiveMaintenanceEndsAt
            );

            if (effectiveMaintenanceEndsAt) {
                this.scheduleMaintenanceEnd(area, this.parseMaintenanceDate(effectiveMaintenanceEndsAt));
            }

            return;
        }

        state.forcedSoftMaintenanceKey = maintenanceKey;
        this.clearSoftMaintenanceStartTimeout(area);
        this.syncActiveOnlineFlowState(area);
        this.setMaintenanceToTrue(area, softMaintenanceStartsAt, hardMaintenanceStartsAt, effectiveMaintenanceEndsAt);
        this.scheduleMaintenanceEnd(area, this.parseMaintenanceDate(effectiveMaintenanceEndsAt));
    }

    private handleSoftMaintenanceContext(area: MaintenanceArea, maintenanceKey: string): void {
        if (area === 'app') return;

        const state = this.maintenanceStates[area];
        const currentOnlineGame = this.onlinePlayOnlineGameService.currentOnlineGame();
        const currentPreGame = this.onlinePlayPreGameService.currentPreGame();

        this.clearRankedMatchmaking();

        if (area === 'online_play') return;

        const inAffectedOnlineGame =
            area === 'gameplay' ? !!currentOnlineGame : this.onlinePlayOnlineGameService.isRankedGame();
        const inAffectedPreGame =
            !!currentPreGame &&
            (area === 'gameplay' || currentPreGame.gameplay_settings.gameplay_type === 'ranked_game');

        if (inAffectedOnlineGame || inAffectedPreGame) {
            state.blockedForSoftMaintenance = false;

            if (state.shownSoftMaintenanceContinuationKey !== maintenanceKey) {
                state.shownSoftMaintenanceContinuationKey = maintenanceKey;
                this.showSoftMaintenanceContinuationAlert(area);
            }

            return;
        }

        if (state.blockedForSoftMaintenance) return;

        state.blockedForSoftMaintenance = true;

        if (area === 'gameplay') {
            this.onlinePlaySocketService.pauseOnlinePlay();
        }
    }

    private handleActiveSoftMaintenanceContext(
        area: MaintenanceArea,
        maintenance: MaintenanceConfig | undefined
    ): void {
        if (!maintenance?.in_maintenance) return;

        const hardStartsAt: Date | null = this.getHardMaintenanceStartDate(maintenance);
        const now: number | null = this.clockService.getNow();
        if (hardStartsAt && now != null && hardStartsAt.getTime() <= now) return;

        const maintenanceKey = this.getSoftMaintenanceStartDate(maintenance)?.toISOString() ?? 'in_maintenance';
        this.handleSoftMaintenanceContext(area, maintenanceKey);
    }

    private clearRankedMatchmaking(): void {
        if (!this.rankedQueueService.inQueue() && !this.rankedQueueService.queuePairConfirmationState()) return;

        if (!this.onlinePlayMaintenanceStateService.inHardMaintenance()) {
            void this.rankedQueueService.leaveQueue();
        }

        this.rankedQueueService.clearQueueState();
    }

    private showSoftMaintenanceContinuationAlert(area: MaintenanceArea): void {
        const minutesUntilHardMaintenance = this.getMinutesUntilHardMaintenance(area);
        const hardStartsAt = this.getHardMaintenanceStartDate(this.getMaintenance(area));

        if (hardStartsAt && minutesUntilHardMaintenance != null) {
            this.syncWarningScheduleState(area, hardStartsAt.getTime());
            this.maintenanceStates[area].shownWarnings.add(minutesUntilHardMaintenance);
        }

        this.alertService.createAlert({
            id: `${this.getMaintenanceAlertIdPrefix(area)}-soft-maintenance-started`,
            title: this.getSoftMaintenanceContinuationTitle(area),
            text: this.getSoftMaintenanceContinuationText(area, minutesUntilHardMaintenance),
            ...this.getMaintenanceMoreInfoAction(this.getMaintenance(area)),
            icon: 'warning',
            timer: 10000,
            showCloseButton: true,
        });
    }

    private forceStartHardMaintenance(
        area: MaintenanceArea,
        maintenanceKey: string,
        maintenance: MaintenanceConfig | undefined,
        softMaintenanceStartsAt: string | null = null,
        hardMaintenanceStartsAt: string | null = null,
        maintenanceEndsAt: string | null = null
    ): void {
        const state = this.maintenanceStates[area];
        const effectiveMaintenanceEndsAt = maintenanceEndsAt ?? maintenance?.maintenance_ends_at ?? null;

        if (area === 'app') {
            const startKey = this.getSoftMaintenanceStartDate(maintenance)?.toISOString() ?? null;
            state.scheduledSoftMaintenanceKey = startKey;
            state.scheduledHardMaintenanceKey = startKey;
        }

        if (area === 'gameplay') this.onlinePlayMaintenanceStateService.setHardMaintenanceActive(true);
        if (area === 'ranking') this.onlinePlayMaintenanceStateService.setRankedHardMaintenanceActive(true);

        if (state.forcedHardMaintenanceKey === maintenanceKey) return;

        state.forcedHardMaintenanceKey = maintenanceKey;
        this.clearRankedMatchmaking();
        this.clearSoftMaintenanceStartTimeout(area);
        this.clearHardMaintenanceStartTimeout(area);
        this.setMaintenanceToTrue(area, softMaintenanceStartsAt, hardMaintenanceStartsAt, effectiveMaintenanceEndsAt);
        this.scheduleMaintenanceEnd(area, this.parseMaintenanceDate(effectiveMaintenanceEndsAt));

        if (this.shouldForceQuitOnlineGame(area)) {
            this.forceQuitOnlineGameIfNeeded(area);
        }

        this.showMaintenanceStarted(area, maintenance);
    }

    private showMaintenanceStarted(area: MaintenanceArea, maintenance: MaintenanceConfig | undefined): void {
        if (!this.shouldShowMaintenanceAlerts(area)) return;

        this.alertService.createAlert({
            id: `${this.getMaintenanceAlertIdPrefix(area)}-maintenance-started`,
            title: this.getMaintenanceStartedTitle(area),
            text: maintenance?.maintenance_message?.trim() || this.getMaintenanceStartedFallbackText(area),
            ...this.getMaintenanceMoreInfoAction(maintenance),
            icon: 'warning',
            timer: null,
            showCloseButton: true,
        });
    }

    private getMaintenanceMoreInfoAction(
        maintenance: MaintenanceConfig | undefined
    ): Pick<AlertOptions, 'confirmButtonText' | 'onConfirm'> {
        const url = maintenance?.more_info_url?.trim();
        if (!url) return {};

        return {
            confirmButtonText: $localize`:@@FOR_MORE_INFORMATION:For more information`,
            onConfirm: () => {
                void Browser.open({ url });
            },
        };
    }

    private forceQuitOnlineGameIfNeeded(area: MaintenanceArea): void {
        if (area === 'app') {
            this.gamesLeaveBusterService.allowedToLeave.set(true);
            this.onlinePlayPreGameService.currentPreGame.set(null);
            void this.redirectToServiceUnavailable();
            return;
        }

        const currentOnlineGame = this.onlinePlayOnlineGameService.currentOnlineGame();
        const currentPreGame = this.onlinePlayPreGameService.currentPreGame();

        if (area !== 'ranking') this.redirectTournamentViewIfNeeded();

        if (currentPreGame && !currentOnlineGame) {
            this.onlinePlayPreGameService.currentPreGame.set(null);
            this.navController.navigateRoot(area === 'ranking' ? '/ranked' : '/online-games', { replaceUrl: true });
        }

        if (!currentOnlineGame) {
            if (area === 'gameplay') this.onlinePlaySocketService.pauseOnlinePlay();
            return;
        }

        this.gamesLeaveBusterService.allowedToLeave.set(true);
        this.onlinePlayOnlineGameService.handleRedirectAfterGame();

        if (area === 'gameplay') {
            setTimeout(() => {
                if (!this.getMaintenance(area)?.in_maintenance) return;

                this.onlinePlaySocketService.pauseOnlinePlay(true);
            }, 500);
        }
    }

    private setMaintenanceToTrue(
        area: MaintenanceArea,
        softMaintenanceStartsAt: string | null = null,
        hardMaintenanceStartsAt: string | null = null,
        maintenanceEndsAt: string | null = null
    ): void {
        this.updateMaintenance(area, (maintenance) => ({
            ...maintenance,
            in_maintenance: true,
            show_notice: false,
            soft_maintenance_starts_at: softMaintenanceStartsAt ?? maintenance.soft_maintenance_starts_at,
            hard_maintenance_starts_at: hardMaintenanceStartsAt ?? maintenance.hard_maintenance_starts_at,
            maintenance_ends_at: maintenanceEndsAt ?? maintenance.maintenance_ends_at,
        }));
    }

    private getSoftMaintenanceStartDate(maintenance: MaintenanceConfig | undefined): Date | null {
        if (!maintenance) {
            return null;
        }

        return this.parseMaintenanceDate(maintenance.soft_maintenance_starts_at);
    }

    private getHardMaintenanceStartDate(maintenance: MaintenanceConfig | undefined): Date | null {
        if (!maintenance) {
            return null;
        }

        return this.parseMaintenanceDate(maintenance.hard_maintenance_starts_at);
    }

    private getMaintenanceEndDate(maintenance: MaintenanceConfig | undefined): Date | null {
        if (!maintenance) {
            return null;
        }

        return this.parseMaintenanceDate(maintenance.maintenance_ends_at);
    }

    private getMaintenanceWarningStartDate(
        maintenance: MaintenanceConfig | undefined,
        now: number | null
    ): Date | null {
        const softStartsAt = this.getSoftMaintenanceStartDate(maintenance);
        const hardStartsAt = this.getHardMaintenanceStartDate(maintenance);

        if (!maintenance?.in_maintenance && (!softStartsAt || now == null || softStartsAt.getTime() > now)) {
            return softStartsAt ?? hardStartsAt;
        }

        return hardStartsAt;
    }

    private getMaintenanceKeyFromStartAt(maintenanceStartsAt: string | null): string | null {
        return this.parseMaintenanceDate(maintenanceStartsAt)?.toISOString() ?? null;
    }

    private parseMaintenanceDate(maintenanceDate: string | null): Date | null {
        if (!maintenanceDate) return null;
        const parsedDate = moment.utc(maintenanceDate);
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    }

    private initStatusListener(): void {
        this.echoService.persistentPublicChannel(
            'global',
            'maintenance.status.updated',
            (event: MaintenanceStatusUpdatedEvent) => {
                this.handleMaintenanceStatusEvent(event);
            },
            () => {
                void this.loadMaintenances().catch(() => undefined);
            }
        );
    }

    private handleMaintenanceStatusEvent(event: MaintenanceStatusUpdatedEvent): void {
        const { area, status, maintenance } = event;
        const wasInMaintenance: boolean = !!this.getMaintenance(area)?.in_maintenance;
        const now: number | null = this.clockService.getNow();
        const softStartsAt = this.getSoftMaintenanceStartDate(maintenance);

        this.updateMaintenance(area, () => maintenance);

        if (
            wasInMaintenance &&
            !maintenance.in_maintenance &&
            (!softStartsAt || (now != null && softStartsAt.getTime() > now))
        ) {
            this.finishMaintenance(area, false);
        }

        const endsAt: Date | null = this.getMaintenanceEndDate(maintenance);
        if (now != null) {
            this.syncMaintenanceSchedule(area, maintenance, now);
            if (endsAt && endsAt.getTime() <= now) return;
        }

        if (status === 'maintenance_in_15_minutes' || status === 'maintenance_in_5_minutes') {
            this.handleMaintenanceWarningStatus(
                area,
                status === 'maintenance_in_15_minutes' ? 15 : 5,
                maintenance.soft_maintenance_starts_at,
                maintenance.hard_maintenance_starts_at,
                maintenance.maintenance_ends_at
            );
        } else if (status === 'in_maintenance') {
            this.handleInMaintenanceStatus(
                area,
                maintenance.soft_maintenance_starts_at,
                maintenance.hard_maintenance_starts_at,
                maintenance.maintenance_ends_at
            );
        }
    }

    private handleMaintenanceWarningStatus(
        area: MaintenanceArea,
        minutesUntilMaintenance: number | null,
        softMaintenanceStartsAt: string | null,
        hardMaintenanceStartsAt: string | null,
        maintenanceEndsAt: string | null
    ): void {
        this.updateMaintenance(area, (maintenance) => {
            const effectiveSoftMaintenanceStartsAt = softMaintenanceStartsAt ?? maintenance.soft_maintenance_starts_at;
            const softMaintenanceDate = this.parseMaintenanceDate(effectiveSoftMaintenanceStartsAt);
            const now = this.clockService.getNow();
            const inSoftMaintenance =
                maintenance.in_maintenance ||
                (softMaintenanceDate != null && now != null && softMaintenanceDate.getTime() <= now);

            return {
                ...maintenance,
                in_maintenance: inSoftMaintenance,
                show_notice: !inSoftMaintenance,
                soft_maintenance_starts_at: effectiveSoftMaintenanceStartsAt,
                hard_maintenance_starts_at: hardMaintenanceStartsAt ?? maintenance.hard_maintenance_starts_at,
                maintenance_ends_at: maintenanceEndsAt ?? maintenance.maintenance_ends_at,
            };
        });

        if (minutesUntilMaintenance == null || this.getMaintenance(area)?.in_maintenance) {
            return;
        }

        const warningStartsAt = this.getMaintenanceWarningStartDate(
            this.getMaintenance(area),
            this.clockService.getNow()
        );
        if (warningStartsAt) this.syncWarningScheduleState(area, warningStartsAt.getTime());

        this.clearWarningTimeout(area, minutesUntilMaintenance);

        if (this.maintenanceStates[area].shownWarnings.has(minutesUntilMaintenance)) {
            return;
        }

        this.maintenanceStates[area].shownWarnings.add(minutesUntilMaintenance);
        this.showMaintenanceWarning(area, minutesUntilMaintenance, this.getMaintenance(area));
    }

    private handleInMaintenanceStatus(
        area: MaintenanceArea,
        softMaintenanceStartsAt: string | null,
        hardMaintenanceStartsAt: string | null,
        maintenanceEndsAt: string | null
    ): void {
        const maintenance = this.getMaintenance(area);
        const effectiveSoftMaintenanceStartsAt =
            softMaintenanceStartsAt ?? maintenance?.soft_maintenance_starts_at ?? null;
        const effectiveHardMaintenanceStartsAt =
            hardMaintenanceStartsAt ?? maintenance?.hard_maintenance_starts_at ?? null;
        const effectiveMaintenanceEndsAt = maintenanceEndsAt ?? maintenance?.maintenance_ends_at ?? null;
        const softMaintenanceKey =
            this.getMaintenanceKeyFromStartAt(effectiveSoftMaintenanceStartsAt) ??
            effectiveSoftMaintenanceStartsAt ??
            'soft_maintenance';

        if (area === 'app') {
            this.forceStartHardMaintenance(
                area,
                this.getMaintenanceKeyFromStartAt(effectiveSoftMaintenanceStartsAt) ?? 'app_maintenance',
                maintenance,
                effectiveSoftMaintenanceStartsAt,
                effectiveHardMaintenanceStartsAt,
                effectiveMaintenanceEndsAt
            );
            this.scheduleMaintenanceRefresh(area);
            return;
        }

        this.forceStartSoftMaintenance(
            area,
            softMaintenanceKey,
            maintenance,
            effectiveSoftMaintenanceStartsAt,
            effectiveHardMaintenanceStartsAt,
            effectiveMaintenanceEndsAt
        );

        const hardMaintenanceDate = this.parseMaintenanceDate(effectiveHardMaintenanceStartsAt);
        const now = this.clockService.getNow();
        if (!hardMaintenanceDate || now == null) return;

        const hardMaintenanceKey =
            this.getMaintenanceKeyFromStartAt(effectiveHardMaintenanceStartsAt) ?? 'hard_maintenance';

        if (hardMaintenanceDate.getTime() <= now) {
            this.forceStartHardMaintenance(
                area,
                hardMaintenanceKey,
                maintenance,
                effectiveSoftMaintenanceStartsAt,
                effectiveHardMaintenanceStartsAt,
                effectiveMaintenanceEndsAt
            );
            return;
        }

        this.scheduleHardMaintenanceStart(area, hardMaintenanceDate.getTime(), hardMaintenanceKey, now);
    }

    private handleMaintenanceFinishedFromConfig(area: MaintenanceArea): void {
        this.finishMaintenance(area, false);
    }

    private handleMaintenanceFinishedFromEndDate(area: MaintenanceArea): void {
        this.finishMaintenance(area, true);
    }

    private finishMaintenance(area: MaintenanceArea, updateLocalMaintenance: boolean): void {
        this.clearTimers(area);
        this.clearMaintenanceRefreshTimeout(area);
        this.clearScheduledMaintenanceState(area);

        if (updateLocalMaintenance) {
            this.updateMaintenance(area, (maintenance) => ({
                ...maintenance,
                in_maintenance: false,
                show_notice: false,
                soft_maintenance_starts_at: null,
                hard_maintenance_starts_at: null,
                maintenance_ends_at: null,
            }));
        }

        if (area !== 'ranking') {
            this.onlinePlaySocketService.refreshSocketConnection();
        }
    }

    private clearTimers(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.warningTimeout) {
            clearTimeout(state.warningTimeout);
            state.warningTimeout = null;
        }

        this.clearSoftMaintenanceStartTimeout(area);
        this.clearHardMaintenanceStartTimeout(area);

        this.clearMaintenanceEndTimeout(area);
    }

    private clearSoftMaintenanceStartTimeout(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.softMaintenanceStartTimeout) {
            clearTimeout(state.softMaintenanceStartTimeout);
            state.softMaintenanceStartTimeout = null;
        }
    }

    private clearHardMaintenanceStartTimeout(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.hardMaintenanceStartTimeout) {
            clearTimeout(state.hardMaintenanceStartTimeout);
            state.hardMaintenanceStartTimeout = null;
        }
    }

    private clearWarningTimeout(area: MaintenanceArea, minutesUntilMaintenance: number): void {
        const state = this.maintenanceStates[area];

        if (state.warningTimeout && !state.shownWarnings.has(minutesUntilMaintenance)) {
            clearTimeout(state.warningTimeout);
            state.warningTimeout = null;
        }
    }

    private clearMaintenanceRefreshTimeout(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.maintenanceRefreshTimeout) {
            clearTimeout(state.maintenanceRefreshTimeout);
            state.maintenanceRefreshTimeout = null;
        }
    }

    private clearMaintenanceEndTimeout(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        if (state.maintenanceEndTimeout) {
            clearTimeout(state.maintenanceEndTimeout);
            state.maintenanceEndTimeout = null;
        }
    }

    private clearScheduledMaintenanceState(area: MaintenanceArea): void {
        const state = this.maintenanceStates[area];

        state.scheduledSoftMaintenanceKey = null;
        state.scheduledHardMaintenanceKey = null;
        state.scheduledMaintenanceEndKey = null;
        state.scheduledWarningKey = null;
        state.shownWarnings.clear();
        state.forcedSoftMaintenanceKey = null;
        state.forcedHardMaintenanceKey = null;
        state.shownSoftMaintenanceContinuationKey = null;
        state.blockedForSoftMaintenance = false;
        this.resetHardMaintenanceState(area);
    }

    private syncActiveOnlineFlowState(area: MaintenanceArea): void {
        if (area === 'ranking' || area === 'app') return;

        this.onlinePlayMaintenanceStateService.setInOnlineGame(!!this.onlinePlayOnlineGameService.currentOnlineGame());
        this.onlinePlayMaintenanceStateService.setInPreGame(!!this.onlinePlayPreGameService.currentPreGame());
    }

    private resetHardMaintenanceState(area: MaintenanceArea): void {
        if (area === 'ranking') this.onlinePlayMaintenanceStateService.setRankedHardMaintenanceActive(false);

        if (area === 'gameplay') {
            this.onlinePlayMaintenanceStateService.setHardMaintenanceActive(false);
        }
    }

    private shouldShowMaintenanceAlerts(area: MaintenanceArea): boolean {
        if (area !== 'app' && this.getMaintenance('app')?.in_maintenance) return false;

        switch (area) {
            case 'app':
            case 'online_play':
                return true;
            case 'gameplay': {
                return (
                    !!this.onlinePlayOnlineGameService.currentOnlineGame() ||
                    this.onlinePlayGlobalLobbyService.inGlobalLobby()
                );
            }
            case 'ranking': {
                return this.onlinePlayOnlineGameService.isRankedGame() || this.router.url.startsWith('/ranked');
            }
        }
    }

    private getNextWarningMinute(
        area: MaintenanceArea,
        maintenanceStartsAt: number,
        now: number
    ): WarningMinute | null {
        const state = this.maintenanceStates[area];

        for (const warningMinute of this.warningMinutes) {
            if (state.shownWarnings.has(warningMinute)) {
                continue;
            }

            const delay = maintenanceStartsAt - now - warningMinute * 60 * 1000;
            if (delay >= 0) {
                return warningMinute;
            }
        }

        return null;
    }

    private syncWarningScheduleState(area: MaintenanceArea, maintenanceStartsAt: number): void {
        const state = this.maintenanceStates[area];
        const warningKey = new Date(maintenanceStartsAt).toISOString();
        if (state.scheduledWarningKey === warningKey) return;

        if (state.warningTimeout) {
            clearTimeout(state.warningTimeout);
            state.warningTimeout = null;
        }

        state.scheduledWarningKey = warningKey;
        state.shownWarnings.clear();
    }

    private updateMaintenance(
        area: MaintenanceArea,
        updater: (maintenance: OnlineMaintenanceBase) => OnlineMaintenanceBase
    ): void {
        this.appConfigsService.updateMaintenance(area, updater);
    }

    private getMaintenance(area: MaintenanceArea): MaintenanceConfig | undefined {
        return this.appConfigsService.appConfigs().maintenances?.[area];
    }

    private shouldForceQuitOnlineGame(area: MaintenanceArea): boolean {
        switch (area) {
            case 'app':
            case 'gameplay':
            case 'online_play':
                return true;
            case 'ranking':
                return (
                    this.onlinePlayOnlineGameService.isRankedGame() ||
                    this.onlinePlayPreGameService.currentPreGame()?.gameplay_settings.gameplay_type === 'ranked_game'
                );
        }
    }

    private createMaintenanceScheduleState(): MaintenanceScheduleState {
        return {
            warningTimeout: null,
            softMaintenanceStartTimeout: null,
            hardMaintenanceStartTimeout: null,
            maintenanceEndTimeout: null,
            maintenanceRefreshTimeout: null,
            maintenanceRefreshInProgress: false,
            scheduledSoftMaintenanceKey: null,
            scheduledHardMaintenanceKey: null,
            scheduledMaintenanceEndKey: null,
            scheduledWarningKey: null,
            shownWarnings: new Set(),
            forcedSoftMaintenanceKey: null,
            forcedHardMaintenanceKey: null,
            shownSoftMaintenanceContinuationKey: null,
            blockedForSoftMaintenance: false,
        };
    }

    private getMaintenanceAlertIdPrefix(area: MaintenanceArea): string {
        return area;
    }

    private getMaintenanceWarningTitle(area: MaintenanceArea, minutesUntilMaintenance: number): string {
        switch (area) {
            case 'app':
                return this.translateService.instant('APP_MAINTENANCE_STARTS_IN_MINUTES', {
                    maintenanceMinutes: minutesUntilMaintenance,
                });
            case 'gameplay':
            case 'online_play':
                return this.translateService.instant('ONLINE_PLAY_MAINTENANCE_STARTS_IN_MINUTES', {
                    minutes: minutesUntilMaintenance,
                });
            case 'ranking':
                return this.translateService.instant('RANKED_MAINTENANCE_STARTS_IN_MINUTES', {
                    minutes: minutesUntilMaintenance,
                });
        }
    }

    private getMaintenanceWarningFallbackText(area: MaintenanceArea): string {
        switch (area) {
            case 'app':
                return $localize`:@@APP_MAINTENANCE_WARNING:DartCounter will be temporarily unavailable during maintenance.`;
            case 'gameplay':
            case 'online_play':
                return $localize`:@@ONLINE_PLAY_MAINTENANCE_FORCE_QUIT_WARNING:Active online games will be closed when maintenance starts.`;
            case 'ranking':
                return $localize`:@@RANKED_MAINTENANCE_FORCE_QUIT_WARNING:Ranked queue and Ranked games will be unavailable when maintenance starts.`;
        }
    }

    private getMaintenanceStartedTitle(area: MaintenanceArea): string {
        switch (area) {
            case 'app':
                return $localize`:@@APP_MAINTENANCE_STARTED:DartCounter is under maintenance`;
            case 'gameplay':
            case 'online_play':
                return $localize`:@@ONLINE_PLAY_MAINTENANCE_STARTED:Online play is under maintenance`;
            case 'ranking':
                return $localize`:@@RANKED_MAINTENANCE_STARTED:Ranked is under maintenance`;
        }
    }

    private getMaintenanceStartedFallbackText(area: MaintenanceArea): string {
        switch (area) {
            case 'app':
                return $localize`:@@APP_MAINTENANCE_STARTED_DESCRIPTION:DartCounter is temporarily unavailable while maintenance is in progress.`;
            case 'gameplay':
            case 'online_play':
                return $localize`:@@ONLINE_PLAY_MAINTENANCE_STARTED_DESCRIPTION:Online play is temporarily unavailable while maintenance is in progress.`;
            case 'ranking':
                return $localize`:@@RANKED_MAINTENANCE_STARTED_DESCRIPTION:Ranked is temporarily unavailable while maintenance is in progress.`;
        }
    }

    private getSoftMaintenanceContinuationTitle(area: MaintenanceArea): string {
        switch (area) {
            case 'app':
                return this.getMaintenanceStartedTitle(area);
            case 'gameplay':
            case 'online_play':
                return $localize`:@@ONLINE_PLAY_SOFT_MAINTENANCE_STARTED:Online play maintenance has started`;
            case 'ranking':
                return $localize`:@@RANKED_SOFT_MAINTENANCE_STARTED:Ranked maintenance has started`;
        }
    }

    private getSoftMaintenanceContinuationText(
        area: MaintenanceArea,
        minutesUntilHardMaintenance: number | null
    ): string {
        if (minutesUntilHardMaintenance != null) {
            const minutes = minutesUntilHardMaintenance;

            switch (area) {
                case 'app':
                    return this.getMaintenanceStartedFallbackText(area);
                case 'gameplay':
                case 'online_play':
                    return this.translateService.instant(
                        'CONTINUE_PLAYING_ONLINE_GAME_BEFORE_FULL_MAINTENANCE_MINUTES',
                        {
                            minutes: minutes,
                        }
                    );
                case 'ranking':
                    return this.translateService.instant(
                        'CONTINUE_PLAYING_RANKED_GAME_BEFORE_FULL_MAINTENANCE_MINUTES',
                        {
                            minutes: minutes,
                        }
                    );
            }
        }

        switch (area) {
            case 'app':
                return this.getMaintenanceStartedFallbackText(area);
            case 'gameplay':
            case 'online_play':
                return $localize`:@@CONTINUE_PLAYING_ONLINE_GAME_UNTIL_FULL_MAINTENANCE:You can continue playing this online game until full maintenance starts.`;
            case 'ranking':
                return $localize`:@@CONTINUE_PLAYING_RANKED_GAME_UNTIL_FULL_MAINTENANCE:You can continue playing this Ranked game until full maintenance starts.`;
        }
    }

    private getMinutesUntilHardMaintenance(area: MaintenanceArea): number | null {
        return this.getMinutesUntilMaintenance(this.getHardMaintenanceStartDate(this.getMaintenance(area)));
    }

    private getMinutesUntilMaintenance(maintenanceStartsAt: Date | null): number | null {
        const now = this.clockService.getNow();
        if (!maintenanceStartsAt || now == null) return null;

        return Math.max(0, Math.ceil((maintenanceStartsAt.getTime() - now) / (60 * 1000)));
    }

    private async redirectToServiceUnavailable(): Promise<void> {
        await this.modalService.dismissAll('app-maintenance');
        await this.navController.navigateRoot('/service-unavailable', { replaceUrl: true });
    }

    private redirectTournamentViewIfNeeded(): void {
        const isOnTournamentRoute = this.router.url.startsWith('/tournaments');
        if (!isOnTournamentRoute) return;
        this.navController.navigateRoot('/tournaments', { replaceUrl: true });
    }
}
